import { useCallback, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { useApiConfig, type MutationApiConfig } from "@visioni/a3s-client-api/app";
import type { AssetCameraKey, SiteId } from "@visioni/a3s-schema";
import type { RecordActivityBody } from "../../client-messages/record-activity";
import { recordActivityMutation } from "../queries";

export interface RecordCameraActivityParams {
  readonly siteId: SiteId;
  readonly assetCameraKey: AssetCameraKey;
  readonly body: RecordActivityBody;
}

/**
 * {@link useRecordActivity} for screens that span cameras.
 *
 * `useRecordActivity` fixes the camera when the hook mounts, which suits a
 * camera-scoped screen. A tenant-wide list (e.g. clip compilations) acts on
 * whichever row the user picked, so here the camera travels with each call.
 *
 * Fire-and-forget: a failed record is logged, never surfaced — an audit write
 * must not get in the way of the user watching the footage.
 */
export function useRecordCameraActivity(): (params: RecordCameraActivityParams) => void {
  const apiConfig = useApiConfig();

  const mutationConfig: MutationApiConfig = useMemo(
    () => ({
      tenantId: apiConfig.tenant?.tenantId,
      apiBaseUrl: apiConfig.tenant?.apiBaseUrl,
      authenticatedFetch: apiConfig.authenticatedFetch,
    }),
    [apiConfig.tenant, apiConfig.authenticatedFetch],
  );

  const { mutate } = useMutation({
    mutationFn: ({ siteId, assetCameraKey, body }: RecordCameraActivityParams) =>
      recordActivityMutation(mutationConfig, { siteId, assetCameraKey }).mutationFn(body),
    retry: false,
  });

  return useCallback(
    (params: RecordCameraActivityParams) =>
      mutate(params, {
        onError: (error) => {
          console.warn(`Failed to record ${params.body.kind} activity:`, error);
        },
      }),
    [mutate],
  );
}
